/**
 * useEllaRadioContinuation — Global hook that ensures Ella Radio
 * auto-continuation runs on whichever device is the active controller.
 *
 * Mounted in AppLayout so it works regardless of which page is open.
 * The MaestroRadioPage still has its own continuation logic for when
 * the page is mounted (with tuning, tweaks, ambient context). This
 * hook is the safety net for when the radio page is NOT mounted.
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { isRadioRefillActive } from '../radio/radioRefill';
import { isEllaRadioBuildPollActive } from '../radio/ellaRadioBuildPoll';
import { ellaTracksToQueueFormat } from '../radio/ellaQueueFormat';

let _fetching = false;

export function useEllaRadioContinuation(): void {
  const queueIndex = useMaestroStore(s => s.queueIndex);
  const queueLength = useMaestroStore(s => s.queue.length);
  const playbackState = useMaestroStore(s => s.playbackState);
  const lastFetchRef = useRef(0);

  useEffect(() => {
    // Allow continuation even when paused — keep the queue topped up
    // so recovery from renderer loss doesn't hit an empty queue.
    if (queueIndex < 0) return;
    if (playbackState !== 'playing' && playbackState !== 'paused') return;

    const isEllaRadio = sessionStorage.getItem('active_ella_radio') === 'true';
    const playlistId = sessionStorage.getItem('active_smart_playlist');
    if (!isEllaRadio || !playlistId) return;

    // MaestroRadioPage registers radioRefill with full tuning/history — avoid duplicate continue calls.
    if (isRadioRefillActive()) return;
    if (isEllaRadioBuildPollActive()) return;

    // Don't continue on observer devices — the controller handles it
    try {
      if (getSyncHandler().isObserving()) return;
    } catch {}

    const remaining = queueLength - queueIndex - 1;
    if (remaining > 5 || _fetching) return;

    // Debounce: don't fetch more than once per 20 seconds
    if (Date.now() - lastFetchRef.current < 20_000) return;
    lastFetchRef.current = Date.now();
    _fetching = true;

    console.log('[EllaRadioContinuation] Queue low (' + remaining + ' remaining) — fetching more tracks');

    import('../services/smartPlaylistService').then(async ({ smartPlaylistService }) => {
      try {
        const store = useMaestroStore.getState();
        const playedIds = store.queue
          .slice(0, queueIndex + 1)
          .map((item: any) => item?.track?.id || item?.track?.trackId)
          .filter(Boolean)
          .map(String);

        const res = await smartPlaylistService.continue(playlistId, {
          playedTrackIds: playedIds,
          skippedTrackIds: [],
        });

        if (res.tracks?.length > 0) {
          const { QueueController } = await import('../transport/QueueController');
          QueueController.appendToQueue(ellaTracksToQueueFormat(res.tracks), 'useEllaRadioContinuation');
          console.log('[EllaRadioContinuation] Appended', res.tracks.length, 'tracks to queue');
        }
      } catch (e) {
        const msg = String((e as Error)?.message || e || '');
        if (msg.includes('API 409')) {
          console.log('[EllaRadioContinuation] continue deferred (409) — build or server backoff');
        } else {
          console.error('[EllaRadioContinuation] Fetch failed:', e);
        }
      } finally {
        _fetching = false;
      }
    });
  }, [queueIndex, queueLength, playbackState]);
}
